import { getFlowSinkIds, queryRAGFlow, setRAGQuery } from 'js/actions/rag';
import { QUERY_RAG_FLOW_REQUEST } from 'js/constants/actionTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useAPIUsage } from 'new/components/flows/types/genai_rag/context/APIUsage/useAPIUsage';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import useEntities from 'new/state/entities/repository/useEntities';
import useRequests from 'new/state/request/repository/useRequests';
import React, { memo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NodeBox } from '../components/NodeBox';
import { ResourceName } from '../components/ResourceName';
import classes from './QueryPrompt.module.scss';
import UIUtils from 'js/lib/UIUtils';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';

interface QueryPromptProps {
  data: any;
}

export const QueryPrompt = memo((props: QueryPromptProps) => {
  const { data } = props;
  const draft = useDraftContext();
  const { llm, setLLM } = useAPIUsage();
  const dispatch = useAppDispatch();
  const { showError } = useRequests();
  const isLoading = useIsLoading(QUERY_RAG_FLOW_REQUEST);
  const [isOptimisticLoading, setIsOptimisticLoading] = useState(false);
  const query = useSelector((state: any) => state.rag.query?.[data.origin_node_id]) || '';

  const flowId = useRagFlowId();

  const { data: flowNodes } = useEntities('flowNodes');
  const { data: sinks } = useEntities('sinks');
  const flow = flowNodes[flowId || data.origin_node_id] || {};
  // find all the LLM sinks
  const llmSinkIds = getFlowSinkIds(flow, flowNodes);

  const retrieversCount =
    (flow?.linked_flows?.length || 0) +
    (draft.draftState.flowsPendingLinking?.length || 0) +
    (draft.draftState.dataSetsPendingLinking?.length || 0);

  // Reset optimistic loading state when actual loading state changes to false
  useEffect(() => {
    if (!isLoading) {
      setIsOptimisticLoading(false);
    }
  }, [isLoading]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendQuery();
    }
  };

  const onChangeQuery = (e) => {
    dispatch(setRAGQuery(data.origin_node_id, e.target.value));
  };

  const onSendQuery = () => {
    const messages = [];
    if (!query) {
      messages.push('enter a query');
    }
    if (!retrieversCount) {
      messages.push('configure at least one Data Retriever');
    }
    if (!llmSinkIds.length) {
      messages.push('set up at least one LLM Processor');
    }
    if (messages.length) {
      showError(`Please ${UIUtils.formatList(messages, ', ', ' and ')}`);
      return;
    }

    // Set optimistic loading state immediately
    setIsOptimisticLoading(true);

    if (!isLoading) {
      dispatch(queryRAGFlow(data.origin_node_id, query, draft));
    }
  };

  // find all the LLM sinks
  const llmSinks = getFlowSinkIds(flow, flowNodes).map((id) => sinks[id]) || [];

  const showAPI = () => {
    if (llm) {
      return;
    }
    setLLM(llmSinks[0]);
  };

  const HowToUseWrapper = !llmSinks.length ? Tooltip : React.Fragment;
  const tooltipProps = !llmSinks.length ? { title: 'Set up at least one LLM Processor' } : {};

  // Use either the actual loading state or the optimistic loading state
  const isButtonLoading = isLoading || isOptimisticLoading;

  return (
    <NodeBox data={data} className={classes.container}>
      <ResourceName {...props} hideActions>
        QUERY PROMPT
      </ResourceName>
      {/* TODO: commenting this out until we support inserting nodes */}
      {/* <ActionMenu>
        <ActionItem onClick={() => {}}>Add Query Rewriter</ActionItem>
      </ActionMenu> */}
      <div className={classNames(classes.queryPromptContainer, 'nodrag nowheel')}>
        <NodeBox.Title>Query</NodeBox.Title>
        <Stack marginBlock="8px" gap="8px">
          <textarea
            className={classes.input}
            placeholder="Provide a question or topic for retrieval and analysis  "
            value={query}
            onKeyDown={onKeyDown}
            onChange={onChangeQuery}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <HowToUseWrapper {...(tooltipProps as { title: string })}>
            <Button
              className={classes.apiButton}
              onClick={showAPI}
              color={llm?.id === data?.id ? 'primary' : 'basic'}
              size="small"
              variant={llm?.id === data?.id ? 'ghost' : 'outlined'}
            >
              How to Use
            </Button>
          </HowToUseWrapper>
          <Button variant="contained" size="small" disabled={isButtonLoading} onClick={onSendQuery}>
            {isButtonLoading ? 'Processing...' : ' Send Query '}
          </Button>
        </Stack>
      </div>
    </NodeBox>
  );
});
